import { FC, useEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'next-i18next'

import { SecondaryButton, TertiaryButton, Dropdown } from 'ui'
import PriceFilter from './PriceFilter'
import BedBathFilter from './BedBathFilter'
import FavoriteToggle from './FavoriteToggle'
import {
  DialogOverlay,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogFooter,
} from '../Dialog'
import {
  ISortByMethodOption,
  useTrackingBedBathFilter,
  useTrackingPriceFilter,
  useTrackingSort,
} from './index'

import {
  SortByState,
  useListingsState,
} from '../../services/context/ListingsContext'
import { useMarketParam } from '../../hooks/use-market-param'
import { usePreferredMarketLocalStorage } from '../../hooks/use-preferred-market'
import { MARKET_OPTIONS } from '../../utils/markets'

const MobileDialog: FC = () => {
  const { t } = useTranslation('common')
  const trackPriceFilter = useTrackingPriceFilter()
  const trackBedBathsFilter = useTrackingBedBathFilter()
  const trackSort = useTrackingSort()

  const { state, routerPushAllFilters, routePushSortMethod, routePushMarket } =
    useListingsState()
  const {
    priceMin,
    priceMax,
    bedsMin,
    bathsMin,
    address,
    sortByMethod,
    favorites,
  } = state
  const [localPriceMin, setLocalPriceMin] = useState<number | null>(priceMin)
  const [localPriceMax, setLocalPriceMax] = useState<number | null>(priceMax)
  const [localBedsMin, setLocalBedsMin] = useState<number>(bedsMin)
  const [localBathsMin, setLocalBathsMin] = useState<number>(bathsMin)
  const [localAddress, setLocalAddress] = useState<string>(address)
  const [localFavorites, setLocalFavorites] = useState<boolean | null>(
    favorites
  )

  const market = useMarketParam()
  const [, setPreferredMarket] = usePreferredMarketLocalStorage()

  useEffect(() => {
    setLocalPriceMin(priceMin)
    setLocalPriceMax(priceMax)
    setLocalBedsMin(bedsMin)
    setLocalBathsMin(bathsMin)
    setLocalAddress(address)
    setLocalFavorites(favorites)
  }, [priceMin, priceMax, bedsMin, bathsMin, address, favorites])

  const sortByMethodOptions: ISortByMethodOption[] = [
    { label: t('ui.time-on-market-asc'), value: 'time_on_market_asc' },
    { label: t('ui.time-on-market-desc'), value: 'time_on_market_desc' },
    { label: t('ui.price-high-to-low'), value: 'price_high_to_low' },
    { label: t('ui.price-low-to-high'), value: 'price_low_to_high' },
  ]

  const formatStandardSearchProperties = () => ({
    search_term: address,
    min_list_price: priceMin,
    max_list_price: priceMax,
    min_bedroom_count: bedsMin,
    min_bathroom_count: bathsMin,
  })

  return (
    <div className="lg:hidden">
      <Dialog.Root
        onOpenChange={(open) => {
          if (!open) {
            routerPushAllFilters(
              localBedsMin,
              localBathsMin,
              localPriceMin,
              localPriceMax,
              localAddress,
              localFavorites
            )

            const standardSearchProperties = formatStandardSearchProperties()

            if (localPriceMax !== priceMax || localPriceMin !== priceMin) {
              trackPriceFilter(
                localPriceMin,
                localPriceMax,
                standardSearchProperties
              )
            }

            if (localBedsMin !== bedsMin || localBathsMin !== bathsMin) {
              trackBedBathsFilter(
                localBedsMin,
                localBathsMin,
                standardSearchProperties
              )
            }
          }
        }}
      >
        <Dialog.Trigger asChild>
          <TertiaryButton>{t('ui.filters')}</TertiaryButton>
        </Dialog.Trigger>
        <Dialog.Portal>
          <DialogOverlay>
            <DialogContent>
              <DialogTitle>{t('ui.filters')}</DialogTitle>
              <Dialog.Description className="sr-only">
                {t('home-page.filtersSrDescription')}
              </Dialog.Description>
              <DialogBody>
                <ul className="space-y-20">
                  <li className="sm:hidden">
                    <Dropdown
                      title={`${t('ui.sort')}:`}
                      items={sortByMethodOptions}
                      value={sortByMethod}
                      onSelect={(value) => {
                        routePushSortMethod(value as SortByState)
                        const standardSearchProperties =
                          formatStandardSearchProperties()

                        trackSort(
                          value as SortByState,
                          standardSearchProperties
                        )
                      }}
                      align="end"
                    />
                  </li>
                  <li className="sm:hidden">
                    <Dropdown
                      title={`Market:`}
                      items={MARKET_OPTIONS}
                      value={market}
                      onSelect={(value) => {
                        routePushMarket(value)
                        setPreferredMarket(value)
                      }}
                      align="end"
                    />
                  </li>
                  <li>
                    <BedBathFilter
                      key={`${bedsMin}${bathsMin}`}
                      inline
                      beds={localBedsMin}
                      baths={localBathsMin}
                      setBeds={setLocalBedsMin}
                      setBaths={setLocalBathsMin}
                    />
                  </li>
                  <li>
                    <FavoriteToggle
                      expanded
                      isActive={localFavorites ?? false}
                      onToggle={() => {
                        setLocalFavorites(!localFavorites)
                      }}
                    />
                  </li>
                  <li>
                    <PriceFilter
                      key={`${priceMin}${priceMax}`}
                      inline
                      min={localPriceMin}
                      max={localPriceMax}
                      setMin={setLocalPriceMin}
                      setMax={setLocalPriceMax}
                    />
                  </li>
                </ul>
              </DialogBody>
              <DialogFooter>
                <Dialog.Close asChild>
                  <SecondaryButton>{t('ui.done')}</SecondaryButton>
                </Dialog.Close>
              </DialogFooter>
            </DialogContent>
          </DialogOverlay>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}

export default MobileDialog
